import { FC, useState } from "react";

import { Button, Card } from "react-bootstrap";

import { FileText, Search, Plus, Loader2, LoaderCircle } from "lucide-react";

import { useProductManagementContext } from "../hooks/useProductManagementContext";
import { useQuoteAssociation } from "../hooks/useQuoteAssociation";

const ProductCatalog: FC = () => {
  const { quoteOption } = useQuoteAssociation();
  const [showProductDetails, setShowProductDetails] = useState(false);
  
  const {
    filteredProducts,
    selectedProducts,
    hasMoreProducts,
    loadingProducts,
    loadingMoreProducts,
    productSearchQuery,
    setProductSearchQuery,
    handleProductToggle,
    handleQuantityChange,
    handleResetProductSelection,
    handleFetchMoreProducts,
    getSelectedProductsCount,
    calculateTotal,
  } = useProductManagementContext();

  if (quoteOption !== "new") return null;

  return (
    <div className="mt-4 space-y-4">
      <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center space-x-2">
            <Plus className="w-4 h-4 text-purple-600" />
            <span className="text-sm font-medium text-purple-700">
              Nouveau devis
            </span>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowProductDetails(!showProductDetails)}
            className=" text-xs text-purple-600 hover:text-purple-700 border-purple-300 hover:border-purple-400"
          >
            {showProductDetails
              ? "Masquer les produits"
              : "Sélectionner les produits"}
          </Button>
        </div>
        <p className="text-xs text-purple-600 mb-3">
          Sélectionnez les produits à inclure dans le nouveau devis.
        </p>

        {getSelectedProductsCount() > 0 && (
          <div className="bg-white p-2 rounded border border-purple-200">
            <div className="flex justify-between items-center">
              <span className="text-xs font-medium text-purple-700">
                {getSelectedProductsCount()} produit(s) sélectionné(s)
              </span>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleResetProductSelection}
                className="text-red-600 hover:text-red-700 hover:bg-red-50 px-2 py-1 h-6 text-xs"
              >
                Réinitialiser
              </Button>
              <span className="text-xs font-bold text-purple-700">
                Total: {calculateTotal().toLocaleString("fr-FR")} €
              </span>
            </div>
          </div>
        )}
      </div>

      {showProductDetails && (
        <Card className="border border-purple-200">
          <Card.Header className="pb-3">
            <Card.Title className="text-lg flex items-center space-x-2">
              <FileText className="w-4 h-6 text-purple-600" />
              <span className="text-sm">Catalogue de produits</span>
            </Card.Title>
            <div className="mt-3">
              <div className="relative">
                <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Rechercher par nom, description, catégorie ou ID exact..."
                  value={productSearchQuery}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setProductSearchQuery(e.target.value)
                  }
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm"
                />
              </div>
              {productSearchQuery && (
                <div className="mt-2 text-xs text-gray-500">
                  {filteredProducts.length} produit(s) trouvé(s) pour "
                  {productSearchQuery}"
                </div>
              )}
            </div>
          </Card.Header>
          <Card.Body>
            {productSearchQuery && (
              <div className="space-y-2 max-h-64 overflow-y-auto mb-4">
                {loadingProducts ? (
                  <div className="flex items-center justify-center p-4">
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    <span className="text-sm text-gray-600">
                      Recherche de produits...
                    </span>
                  </div>
                ) : filteredProducts.length > 0 ? (
                  filteredProducts.map((product) => (
                    <div
                      key={product.id}
                      className={`p-3 border rounded-lg cursor-pointer transition-all ${
                        selectedProducts[product.id.toString()]?.selected
                          ? "border-purple-300 bg-purple-50"
                          : "border-gray-200 hover:border-gray-300"
                      }`}
                      onClick={() => handleProductToggle(product.id.toString())}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3 flex-1">
                          <input
                            type="checkbox"
                            checked={
                              selectedProducts[product.id.toString()]?.selected ||
                              false
                            }
                            onChange={() => handleProductToggle(product.id.toString())}
                            className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                            onClick={(e) => e.stopPropagation()}
                          />
                          <div className="flex-1">
                            <div className="font-medium text-sm">
                              {product.reference ? `${product.label} (${product.reference})` : product.label}
                            </div>
                            <div className="text-xs text-gray-500 truncate">
                              {product.description}
                            </div>
                          </div>
                        </div>
                        <div className="text-right ml-2">
                          <div className="text-sm font-bold text-purple-600">
                            {parseFloat(product.price).toLocaleString("fr-FR")} {product.currency}
                          </div>
                          <div className="text-xs text-gray-500">
                            /{product.unit}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-center p-4 text-gray-500 text-sm">
                    Aucun produit trouvé pour cette recherche
                  </div>
                )}
              </div>
            )}

            <div className="space-y-3 max-h-96 overflow-y-auto">
              {!productSearchQuery && (
                <>
                  {loadingProducts ? (
                    <div className="flex items-center justify-center p-4">
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                      <span className="text-sm text-gray-600">
                        Chargement des produits...
                      </span>
                    </div>
                  ) : filteredProducts.length > 0 ? (
                    filteredProducts.map((product) => (
                  <div
                    key={product.id}
                    className={`p-4 border rounded-lg transition-all ${
                      selectedProducts[product.id.toString()]?.selected
                        ? "border-purple-300 bg-purple-50"
                        : "border-gray-200 hover:border-gray-300"
                    }`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex items-start space-x-3 flex-1">
                        <input
                          type="checkbox"
                          checked={
                            selectedProducts[product.id.toString()]?.selected ||
                            false
                          }
                          onChange={() =>
                            handleProductToggle(product.id.toString())
                          }
                          className="mt-1 w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                        />
                        <div className="flex-1">
                            <h4 className="font-medium text-xs">
                            {product.reference ? `${product.label} (${product.reference})` : product.label}
                            </h4>
                          <p className="text-xs text-gray-600 mb-2">
                            {product.description}
                          </p>
                          <div className="flex flex-col items-start  space-x-4">
                            <div className="flex items-center space-x-1">
                            <span className="text-xs font-bold text-purple-600">
                              {parseFloat(product.price).toLocaleString(
                                "fr-FR"
                              )}{" "}
                              {product.currency}
                            </span>
                            <span className="text-xs text-gray-500">
                              /{product.unit}
                            </span>
                            </div>
                            {selectedProducts[product.id.toString()]
                              ?.selected && (
                              <div className="flex items-center">
                                <label
                                  htmlFor={`qty-${product.id}`}
                                  className="text-xs text-gray-600"
                                >
                                  Qté:
                                </label>
                                <div className="flex items-center space-x-1">
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    className="w-6 h-6 p-0 text-xs"
                                    onClick={() =>
                                      handleQuantityChange(
                                        product.id.toString(),
                                        (selectedProducts[product.id.toString()]
                                          ?.quantity || 1) - 1
                                      )
                                    }
                                  >
                                    -
                                  </Button>
                                  <input
                                    id={`qty-${product.id}`}
                                    type="number"
                                    min="1"
                                    value={
                                      selectedProducts[product.id.toString()]
                                        ?.quantity || 1
                                    }
                                    onChange={(e) =>
                                      handleQuantityChange(
                                        product.id.toString(),
                                        Number.parseInt(e.target.value) || 1
                                      )
                                    }
                                    className="w-16 h-6 text-center text-xs border border-gray-300 rounded"
                                  />
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    className="w-6 h-6 p-0 text-xs"
                                    onClick={() =>
                                      handleQuantityChange(
                                        product.id.toString(),
                                        (selectedProducts[product.id.toString()]
                                          ?.quantity || 1) + 1
                                      )
                                    }
                                  >
                                    +
                                  </Button>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))
                  ) : (
                    <div className="text-center p-4 text-gray-500 text-sm">
                      Aucun produit disponible
                    </div>
                  )}

                  {hasMoreProducts && filteredProducts.length > 0 && (
                    <div className="mt-4 text-center">
                      <Button
                        variant="outline"
                        onClick={handleFetchMoreProducts}
                        disabled={loadingMoreProducts}
                        className="w-full"
                      >
                        {loadingMoreProducts ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            Chargement...
                          </>
                        ) : (
                          <>
                            <LoaderCircle className="w-4 h-4 mr-2" />
                            Charger plus de produits
                          </>
                        )}
                      </Button>
                    </div>
                  )}
                </>
              )}
            </div>
          </Card.Body>
        </Card>
      )}
    </div>
  );
};

export default ProductCatalog;
